import { useRef } from "react";
import { Button, Dropdown, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, NavBarSearch, NavPortal } from "components";
import { API_KEY_TYPES } from "models";
import "modules/ApiKeys/ApiKeys.scss";
import { ApiReadingKeysTable, CreateKeyModalForm, RequestLogsModal } from "modules/ApiKeys/components";
import { useApiKeyTypeIdDropdownOptions } from "modules/ApiKeys/components/CreateKeyModal/hooks";
import { API_KEY_MODALS, useApiKeys, useApiKeysFilterHandlers, useKeysFilters } from "modules/ApiKeys/hooks";

const ApiKeys = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const filters = useKeysFilters();
  const { handleSearchTermChange, handleTypeChange } = useApiKeysFilterHandlers();
  const { apiKeyTypeIdDropdownOptionsWithAll } = useApiKeyTypeIdDropdownOptions();
  const {
    showApiKeyModal,
    createApiKeyModal,
    revokeApiKeyModal,
    requestLogsModal,
    handleCloseModal,
    handleOpenModal,
    handleRequestsLogsModal,
    handleRevokeApiKeyModal,
    handleShowApiKeyModal,
    handleRevokeApiKey,
    handleCancelRevokeApiKey,
    handleConfirmShowApiKey,
    handleCancelShowApiKey,
    setApiKeyReasonNote,
    requestLogsID,
    apiKeyReasonNote,
    isRevokeLoading,
    isRevealLoading
  } = useApiKeys();

  return (
    <div ref={containerRef} className="ApiReadingKeys">
      <NavPortal>
        <NavBarSearch handleSearchChange={handleSearchTermChange} value={filters.term} />
      </NavPortal>
      <div className="ApiReadingKeys__filters">
        <Can I="create" the="api-keys">
          <Button icon labelPosition="left" onClick={() => handleOpenModal(API_KEY_MODALS.CreateApiKeyModal)}>
            <Icon className="plus" />
            <span className="ApiReadingKeys__create-text">Create Token</span>
          </Button>
        </Can>
        <div className="ApiReadingKeys__type-container">
          <Dropdown
            selection
            fluid
            selectOnBlur={false}
            placeholder="Select Type"
            options={apiKeyTypeIdDropdownOptionsWithAll}
            onChange={(_, { value }) => handleTypeChange(!value ? null : (value as API_KEY_TYPES))}
          />
        </div>
      </div>
      <ApiReadingKeysTable
        handleShowApiKeyModal={(id: number) => handleShowApiKeyModal(id)}
        handleRequestsLogsModal={(id: number) => handleRequestsLogsModal(id)}
        handleRevokeApiKeyModal={(id: number) => handleRevokeApiKeyModal(id)}
      />
      <CreateKeyModalForm isOpen={createApiKeyModal} onClose={() => handleCloseModal(API_KEY_MODALS.CreateApiKeyModal)} />
      {requestLogsID && <RequestLogsModal isOpen={requestLogsModal} requestLogsId={requestLogsID} onClose={() => handleCloseModal(API_KEY_MODALS.RequestLogsModal)} />}
      <CustomConfirm
        isOpen={revokeApiKeyModal}
        isLoading={isRevokeLoading}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        handleConfirm={handleRevokeApiKey}
        handleCancel={handleCancelRevokeApiKey}
        customTitle="Confirmation required"
        confirmMsg="Are you sure you want to revoke this key? Once revoked it cannot be activated again."
      />
      <CustomConfirm
        note={apiKeyReasonNote}
        isOpen={showApiKeyModal}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isLoading={isRevealLoading}
        isDisabled={!apiKeyReasonNote}
        handleConfirm={handleConfirmShowApiKey}
        handleCancel={handleCancelShowApiKey}
        customTitle="Confirmation required"
        onChangeNote={e => setApiKeyReasonNote(e.target.value)}
        confirmMsg="Access to API keys is locked, please give your reason for displaying it."
        confirmMandatoryNote="Reason for requesting API key."
      />
    </div>
  );
};

export default ApiKeys;
